import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import {
  DistributionFieldType,
  ICommittedItem,
  ICommittedItemExtended,
  ILineItem,
  ILineItemExtended,
  ISubitem,
  ISubitemExtended,
} from '../store/spend/spend.interfaces';
import { CurrentUserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class SpendDistributionService {
  private readonly userService = inject(CurrentUserService);
  private readonly DATE_FORMAT = 'YYYY-MM-DD';

  getDisabledMonths(
    year: number,
    item: ILineItem | ICommittedItem,
    budgetKey: DistributionFieldType,
  ): boolean[] {
    const availableMonths = Array(12).fill(false);
    if (year === 0) {
      return availableMonths;
    }

    const fyStart = this.userService.data.fiscal_year_start - 1;
    const startDate =
      budgetKey === 'monthly_prime' || budgetKey === 'monhtly_prime_actuals'
        ? item.prime_start_date
        : item.start_date;
    const duration =
      budgetKey === 'monthly_prime' || budgetKey === 'monhtly_prime_actuals'
        ? item.prime_duration
        : item.duration;
    const itemStart = dayjs(startDate);
    const itemEnd = dayjs(startDate).add(duration, 'month');

    // set to fiscal year start, just as in the header
    let currentMonth = dayjs(`${year}-01-01`, this.DATE_FORMAT);
    if (fyStart) {
      currentMonth = currentMonth.subtract(12 - fyStart, 'month');
    }

    // set all months that should be available, months by default are unavailable
    availableMonths.forEach((month, index) => {
      const budget = item.budget.find((bud) => bud.year === year);
      const hasFieldValue =
        budget?.[budgetKey][index + 1] !== 0 && budget?.[budgetKey][index + 1] !== undefined;
      // const isManualDistribution = item[distKey] === DISTRIBUTION_TYPES.MANUAL;
      const isAfterStart = currentMonth.isSameOrAfter(itemStart, 'month');
      const isInsideLineDuration = isAfterStart && currentMonth.isBefore(itemEnd, 'month');
      if (isInsideLineDuration || (hasFieldValue && isAfterStart)) {
        availableMonths[index] = true;
      }
      currentMonth = currentMonth.add(1, 'month');
    });

    return availableMonths;
  }

  extendedItemToOriginal(item: ILineItemExtended): ILineItem {
    return {
      id: item.id,
      name: item.name,
      value: item.value,
      project_id: item.project_id,
      start_date: item.start_date,
      duration: item.duration,
      distribution: item.distribution,
      prime_start_date: item.prime_start_date,
      prime_duration: item.prime_duration,
      prime_distribution: item.prime_distribution,
      budget: item.budget,
      commitment_start_date: item.commitment_start_date,
      row_number: item.row_number,
      committed_items: [], // handled in the store
      subitems: item.subitems,
      used_in_pc: item.used_in_pc,
    };
  }

  extendedSubitemToOriginal(item: ISubitemExtended): ISubitem {
    return {
      id: item.id,
      name: item.name,
      value: item.value,
      project_id: item.project_id,
      start_date: item.start_date,
      duration: item.duration,
      distribution: item.distribution,
      prime_start_date: item.prime_start_date,
      prime_duration: item.prime_duration,
      prime_distribution: item.prime_distribution,
      budget: item.budget,
      commitment_start_date: item.commitment_start_date,
      row_number: item.row_number,
      committed_items: [], // handled in the store
      subitems: item.subitems,
      parent_id: item.parent_id,
      is_subitem: item.is_subitem,
      used_in_pc: item.used_in_pc,
    };
  }

  extendedCommitmentItemToOriginal(item: ICommittedItemExtended): ICommittedItem {
    return {
      id: item.id,
      commitment_id: item.commitment_id,
      commitment_name: item.commitment_name,
      commitment_title: item.commitment_title,
      start_date: item.start_date,
      duration: item.duration,
      distribution: item.distribution,
      prime_start_date: item.prime_start_date,
      prime_duration: item.prime_duration,
      prime_distribution: item.prime_distribution,
      budget: item.budget,
      row_number: item.row_number,
      committed_row_number: item.committed_row_number,
      item_id: item.item_id,
      type: item.type,
    };
  }
}
